import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePosts } from '@/hooks/usePosts';
import { usePolling } from '@/hooks/usePolling';
import { PostCard } from '@/components/PostCard';
import BonitaErrorBoundary from '@/components/BonitaErrorBoundary';
import { RisingVoicesStrip } from '@/components/RisingVoicesStrip';
import { WelcomeModal } from '@/components/WelcomeModal';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarDays, Flame, Lock, Loader2, Shield, Sparkles, Users } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fetchStoryPosts } from '@/lib/storyPosts';
import { asPlainText, shouldShowBonitaFeedPost } from '@/lib/safeText';
import { ENERGIES } from '@/components/ComposerModal';
import { PeopleDiscover } from '@/components/community';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateSheet } from '@/context/CreateSheetContext';

const BLOCK_SPARK_COOLDOWN_MS = 6 * 60 * 60 * 1000;

// ── Live Stoop feed card ──────────────────────────────────────────────────────
function LiveStoopCard({ stoop, navigate }) {
  const speakerCount = stoop.speaker_count ?? stoop.speakers?.length ?? 0;
  const listenerCount = stoop.listener_count ?? stoop.listeners?.length ?? 0;
  const title = stoop.title || 'Live on The Stoop';

  return (
    <div
      onClick={() => navigate('/stoop')}
      style={{
        margin: '0 0 1px',
        padding: '14px 16px',
        background: 'linear-gradient(135deg, rgba(240,123,29,0.08) 0%, rgba(28,34,48,0.6) 100%)',
        borderTop: '1px solid rgba(240,123,29,0.15)',
        borderBottom: '1px solid rgba(240,123,29,0.1)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      {/* Live pip */}
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'rgba(240,123,29,0.15)',
        border: '1.5px solid rgba(240,123,29,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
      }}>
        <span style={{ fontSize: '18px', lineHeight: 1 }}>🎙</span>
        <span style={{
          position: 'absolute',
          top: '-2px',
          right: '-2px',
          width: '9px',
          height: '9px',
          borderRadius: '50%',
          background: '#18B574',
          border: '1.5px solid #090B10',
          animation: 'pip 1.4s ease-in-out infinite',
        }} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <span style={{
            fontFamily: "'Azeret Mono', monospace",
            fontSize: '9px',
            letterSpacing: '2px',
            color: '#F07B1D',
            textTransform: 'uppercase',
          }}>Live Now</span>
        </div>
        <p style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: '16px',
          letterSpacing: '1px',
          color: '#EAE0CC',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          margin: 0,
        }}>{title}</p>
        <p style={{
          fontSize: '11px',
          color: '#72788A',
          margin: '2px 0 0',
          fontFamily: "'Instrument Sans', sans-serif",
        }}>
          {speakerCount} speaking · {listenerCount} listening
        </p>
      </div>

      {/* Pull Up arrow */}
      <span style={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: '13px',
        letterSpacing: '1px',
        color: '#F07B1D',
        flexShrink: 0,
      }}>Pull Up →</span>
    </div>
  );
}

// ── HBCU.NEWS "Through Our Lens" feed card ────────────────────────────────
function HBCUNewsCard({ story, navigate }) {
  const headline =
    asPlainText(story.metadata?.headline) ||
    asPlainText(story.content).slice(0, 80) ||
    'From the Yard';
  const summary = story.metadata?.seo_summary;
  const school = asPlainText(story.metadata?.school);
  const category = asPlainText(story.metadata?.category);

  return (
    <div
      onClick={() => navigate(`/post/${story.post_id}`)}
      style={{
        margin: '0 0 1px',
        cursor: 'pointer',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      {story.media_url && (
        <img
          src={story.media_url}
          alt=""
          style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }}
          loading="lazy"
        />
      )}
      <div style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, rgba(9,11,16,0.95) 0%, rgba(15,18,25,0.9) 100%)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: '#F07B1D' }}>
              🎙 HBCU.NEWS
            </span>
            {category && (
              <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1px', color: '#72788A', textTransform: 'uppercase' }}>
                · {category}
              </span>
            )}
          </div>
          {school && (
            <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1px', color: '#3A3F4E' }}>
              {school}
            </span>
          )}
        </div>

        {/* Headline */}
        <p style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: '20px',
          letterSpacing: '1px',
          color: '#EAE0CC',
          lineHeight: '1.1',
          margin: '0 0 6px',
        }}>{headline}</p>

        {summary && typeof summary === 'string' && (
          <p style={{ fontSize: '12px', color: '#C8BCA8', lineHeight: '1.5', margin: '0 0 8px' }}>
            {summary.slice(0, 120)}{summary.length > 120 ? '...' : ''}
          </p>
        )}

        <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '1px', color: '#F07B1D' }}>
          Read on The Word →
        </span>
      </div>
    </div>
  );
}

function NeighborhoodPulseBar({
  feedType,
  stats,
  bonitaPost,
  calendarData,
  liveStoop,
  cookoutAccess,
  voucherProfile,
  navigate,
}) {
  if (feedType === 'cookout') {
    return (
      <div className="md:hidden border-b border-white/10 bg-amber-500/5 px-4 py-3">
        <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-500">
          <Shield className="h-3 w-3" />
          Cookout Pulse
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-none border border-white/10 bg-white/[0.03] px-3 py-2 text-white/75">
            <div className="text-[10px] uppercase tracking-wider text-white/35">Access</div>
            <div>{cookoutAccess ? 'Inside the trusted room' : 'Awaiting a Plate'}</div>
          </div>
          <div className="rounded-none border border-white/10 bg-white/[0.03] px-3 py-2 text-white/75">
            <div className="text-[10px] uppercase tracking-wider text-white/35">Trust</div>
            <div>{voucherProfile ? `Brought in by @${voucherProfile.username}` : 'Trust shapes who gets in'}</div>
          </div>
        </div>
      </div>
    );
  }

  const promptText = bonitaPost
    ? asPlainText(bonitaPost.content).slice(0, 96)
    : 'Bonita is watching the room and can light the first spark if the Block is quiet.';
  const liveCalendar = calendarData?.has_event ? calendarData.event : null;
  const upcoming = !liveCalendar ? calendarData?.upcoming?.[0] : null;

  return (
    <div className="md:hidden border-b border-white/10 bg-white/[0.02] px-4 py-3">
      <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-500">
        <Flame className="h-3 w-3" />
        Neighborhood Pulse
      </div>
      <div className="mb-2 grid grid-cols-3 gap-2 text-center">
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="rounded-none border border-white/10 bg-white/[0.03] px-2 py-2"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/35">Neighbors</div>
          <div className="text-sm text-white">{stats?.active_today ?? '--'}</div>
        </button>
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="rounded-none border border-white/10 bg-white/[0.03] px-2 py-2"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/35">Posts</div>
          <div className="text-sm text-white">{stats?.posts_today ?? '--'}</div>
        </button>
        <button
          type="button"
          onClick={() => navigate(liveStoop ? '/stoop' : '/bonita')}
          className="rounded-none border border-white/10 bg-white/[0.03] px-2 py-2"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/35">{liveStoop ? 'Stoop' : 'Host'}</div>
          <div className="text-sm text-white">{liveStoop ? 'Live now' : 'Bonita'}</div>
        </button>
      </div>
      <button
        type="button"
        onClick={() => navigate('/bonita')}
        className="mb-2 block w-full rounded-none border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-left"
      >
        <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-amber-500">
          <Sparkles className="h-3 w-3" />
          Bonita is hosting
        </div>
        <div className="text-sm leading-relaxed text-white/80">{promptText}{promptText.length >= 96 ? '...' : ''}</div>
      </button>
      <button
        type="button"
        onClick={() => navigate('/calendar')}
        className="block w-full rounded-none border border-white/10 bg-white/[0.03] px-3 py-2 text-left"
      >
        <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-amber-500">
          <CalendarDays className="h-3 w-3" />
          Culture cue
        </div>
        <div className="text-sm text-white/80">
          {liveCalendar
            ? `${liveCalendar.event_name} is live on the calendar today.`
            : upcoming
              ? `${upcoming.event_name} is up next in ${upcoming.days_away} day${upcoming.days_away === 1 ? '' : 's'}.`
              : 'No live date today, but Bonita can still keep the Block warm.'}
        </div>
      </button>
    </div>
  );
}

function DailyRitualCard({ brief, navigate, openCreateSheet, isDark }) {
  if (!brief) return null;

  return (
    <div className={cn("border-b px-4 py-4", isDark ? "border-white/10 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent" : "border-gray-200 bg-gradient-to-br from-amber-100 via-white to-white")}>
      <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-500">
        <Sparkles className="h-3 w-3" />
        {brief.slot_label || 'Bonita Daily Brief'}
      </div>
      <div className={cn("mb-1 font-display text-lg uppercase tracking-wide", isDark ? "text-white" : "text-gray-900")}>{brief.title}</div>
      <p className={cn("mb-3 text-sm leading-relaxed", isDark ? "text-white/70" : "text-gray-600")}>{brief.prompt}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          className="rounded-none bg-amber-500 text-black hover:bg-amber-400"
          onClick={openCreateSheet}
        >
          {brief.cta || 'Drop your take'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="rounded-none border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
          onClick={() => navigate('/bonita')}
        >
          Ask Bonita
        </Button>
      </div>
    </div>
  );
}

function TrustLedgerCard({ voucherProfile, navigate, isDark }) {
  return (
    <div className={cn("border-b px-4 py-4", isDark ? "border-white/10 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent" : "border-gray-200 bg-gradient-to-br from-amber-100 via-white to-white")}>
      <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-500">
        <Shield className="h-3 w-3" />
        Trust Ledger
      </div>
      <div className={cn("mb-1 font-display text-lg uppercase tracking-wide", isDark ? "text-white" : "text-gray-900")}>This room remembers who brought you in.</div>
      <p className={cn("mb-3 text-sm leading-relaxed", isDark ? "text-white/70" : "text-gray-600")}>
        {voucherProfile
          ? `@${voucherProfile.username} put their name on your Plate. In here, trust moves with you, and if things go left, the room expects somebody to come get their mans.`
          : 'Plates carry real trust. The Cookout is warmer than the Block, but it is not consequence-free.'}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="rounded-none border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
          onClick={() => navigate('/vouch')}
        >
          Read the trust rules
        </Button>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { user, refreshUser } = useAuth();
  const { open: openCreateSheet } = useCreateSheet();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

   // Theme detection
  const [isDark, setIsDark] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Theme-aware text classes
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const textMutedClass = isDark ? 'text-white/40' : 'text-gray-500';
  const { posts, loading, fetchFeed } = usePosts();
  const [feedType, setFeedType] = useState(() =>
    searchParams.get('tab') === 'cookout' ? 'cookout' : 'block'
  );
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [cookoutPosts, setCookoutPosts] = useState([]);
  const [cookoutLoading, setCookoutLoading] = useState(false);
  const [energyFilter, setEnergyFilter] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [gateRedeemCode, setGateRedeemCode] = useState(() => searchParams.get('code') || '');
  const [gateRedeeming, setGateRedeeming] = useState(false);
  const [cookoutWelcome, setCookoutWelcome] = useState(false);
  const autoSubmitRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const voucherProfile = user?.vouched_by_profile;

  // Live Stoop (shared polling hook; only active on Block tab)
  const { data: stoopLiveData } = usePolling('/stoop/live', 60000, undefined, {
    enabled: feedType === 'block',
  });
  const { data: pulseStats } = usePolling('/stats/pulse', 60000, undefined, {
    enabled: true,
  });
  const { data: calendarPulseData } = usePolling('/spark/calendar', 300000, undefined, {
    enabled: feedType === 'block',
  });
  const { data: bonitaPulseRows } = usePolling('/posts/user/bonita', 120000, { limit: 5 }, {
    enabled: feedType === 'block',
  });
  const { data: dailyBrief } = usePolling('/bonita/daily-brief', 300000, undefined, {
    enabled: feedType === 'block',
  });
  const liveStoop = useMemo(() => {
    if (feedType !== 'block' || !stoopLiveData) return null;
    const rooms = Array.isArray(stoopLiveData) ? stoopLiveData : (stoopLiveData?.rooms || []);
    return rooms[0] || null;
  }, [feedType, stoopLiveData]);

  // HBCU.NEWS "Through Our Lens" — latest story for feed injection
  const [hbcuStory, setHbcuStory] = useState(null);
  const pulseBonitaPost = useMemo(() => {
    const rows = Array.isArray(bonitaPulseRows) ? bonitaPulseRows : [];
    return rows.find(shouldShowBonitaFeedPost) || null;
  }, [bonitaPulseRows]);

  const syncFeedTab = useCallback((nextFeedType) => {
    setFeedType(nextFeedType);
    const nextParams = new URLSearchParams(searchParams);
    if (nextFeedType === 'cookout') {
      nextParams.set('tab', 'cookout');
    } else {
      nextParams.delete('tab');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (feedType !== 'block') return;
    fetchStoryPosts({ limit: 1 })
      .then((stories) => setHbcuStory(stories[0] || null))
      .catch(() => setHbcuStory(null));
  }, [feedType]);

  // Show welcome modal for first-time users
  useEffect(() => {
    if (user && !user.has_seen_welcome) {
      setShowWelcome(true);
    }
  }, [user]);

  // Fetch cookout posts
  const fetchCookout = useCallback(async (before = null) => {
    if (!user?.is_vouched && !user?.is_admin) return;
    if (!before) setCookoutLoading(true);
    try {
      const params = before ? { before } : {};
      const response = await api.get(`/posts/cookout`, { params, withCredentials: true });
      if (before) {
        setCookoutPosts(prev => [...prev, ...response.data]);
      } else {
        setCookoutPosts(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch cookout:', error);
      if (!before) setCookoutPosts([]);
      return [];
    } finally {
      if (!before) setCookoutLoading(false);
    }
  }, [user?.is_vouched, user?.is_admin]);

  const maybeSeedCalendarContext = useCallback(async () => {
    const dayKey = new Date().toISOString().slice(0, 10);
    const cacheKey = `blvx_calendar_auto_${dayKey}`;
    if (sessionStorage.getItem(cacheKey)) return;
    try {
      await api.post('/spark/calendar/auto', {}, { withCredentials: true });
    } catch {
      // Quiet fail - this is just neighborhood warming, not core navigation.
    } finally {
      sessionStorage.setItem(cacheKey, 'seen');
    }
  }, []);

  /** When The Block has few posts, ask Bonita for one spark — rate-limited per browser session. */
  const maybeSeedBlockFromBonita = useCallback(async (visibleCount) => {
    if (visibleCount >= 5) return;
    const last = sessionStorage.getItem('blvx_block_spark_seed_at');
    if (last && Date.now() - Number(last) < BLOCK_SPARK_COOLDOWN_MS) return;
    try {
      const res = await api.post('/spark/drop', {}, { withCredentials: true });
      if (res.data?.skipped) return;
      sessionStorage.setItem('blvx_block_spark_seed_at', String(Date.now()));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        console.info('[Feed] Bonita spark/drop unavailable — keeping current feed');
      } else if (status === 401 || status === 403) {
        console.warn('[Feed] Auth error on spark/drop');
      } else {
        console.error('[Feed] spark/drop error:', status, err?.message);
      }
    }
  }, []);

  // Load feed function — returns visible post count for seeding decisions
  const loadFeed = useCallback(async (showToast = false) => {
    try {
      let rows;
      if (feedType === 'block') {
        rows = await fetchFeed(null, energyFilter);
      } else {
        rows = await fetchCookout();
      }
      setLastFetch(new Date());
      if (showToast) {
        toast.success('Feed refreshed!');
      }
      return Array.isArray(rows) ? rows.length : 0;
    } catch (error) {
      if (showToast) {
        toast.error('Failed to refresh feed');
      }
      return 0;
    }
  }, [feedType, fetchFeed, fetchCookout, energyFilter]);

  // Load more posts (infinite scroll)
  const loadMore = useCallback(async () => {
    const currentPosts = feedType === 'cookout' ? cookoutPosts : posts;
    if (loadingMore || currentPosts.length === 0) return;

    const lastPost = currentPosts[currentPosts.length - 1];
    const cursor = lastPost?.created_at;
    if (!cursor) return;

    setLoadingMore(true);
    try {
      let newPosts;
      if (feedType === 'block') {
        newPosts = await fetchFeed(cursor, energyFilter);
      } else {
        newPosts = await fetchCookout(cursor);
      }
      // fetchFeed already appends when cursor is provided
      return newPosts;
    } catch {
      // Ignore
    } finally {
      setLoadingMore(false);
    }
  }, [feedType, cookoutPosts, posts, loadingMore, fetchFeed, fetchCookout, energyFilter]);

  // Infinite scroll — detect when near bottom of scroll container
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // When within 2 card heights of the bottom, load more
      const threshold = clientHeight * 2;
      if (scrollHeight - scrollTop - clientHeight < threshold) {
        loadMore();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadMore]);

  // Initial load — optional single Bonita seed when Block is thin, then refresh
  useEffect(() => {
    const initFeed = async () => {
      if (feedType === 'block') {
        await maybeSeedCalendarContext();
      }
      const n = await loadFeed();
      if (feedType === 'block') {
        await maybeSeedBlockFromBonita(n);
        await loadFeed();
      }
    };
    initFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload feed when feed type or energy filter changes
  useEffect(() => {
    if (lastFetch) {
      loadFeed();
      // Scroll back to top on tab/filter change
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedType, energyFilter]);

  // Auto-refresh when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!lastFetch || (new Date() - lastFetch) > 30000) {
          loadFeed();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [lastFetch, loadFeed]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadFeed();
      }
    }, 120000);
    return () => clearInterval(interval);
  }, [loadFeed]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (feedType === 'block') {
      await maybeSeedCalendarContext();
    }
    const n = await loadFeed(true);
    if (feedType === 'block') {
      await maybeSeedBlockFromBonita(n);
      await loadFeed();
    }
    setRefreshing(false);
  };

  // Auto-switch to cookout tab + auto-submit when arriving via deep link
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      syncFeedTab('cookout');
      // Auto-submit after 1.5s so the user sees their code populate first
      autoSubmitRef.current = setTimeout(() => {
        handleRedeemFromGate(null, code);
      }, 1500);
    }
    return () => clearTimeout(autoSubmitRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'cookout' && feedType !== 'cookout') {
      setFeedType('cookout');
    } else if (tab !== 'cookout' && !searchParams.get('code') && feedType !== 'block') {
      setFeedType('block');
    }
  }, [searchParams, feedType]);

  // Cookout gate — redeem plate code inline
  const handleRedeemFromGate = async (e, overrideCode) => {
    if (e) e.preventDefault();
    const code = (overrideCode || gateRedeemCode).trim();
    if (!code) return;
    setGateRedeeming(true);
    try {
      await api.post(`/vouch/plate/redeem?code=${encodeURIComponent(code.toUpperCase())}`);
      setGateRedeemCode('');
      await refreshUser();
      // Show celebratory overlay for 1.5s before revealing the feed
      setCookoutWelcome(true);
      setTimeout(() => setCookoutWelcome(false), 1500);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid plate code');
    } finally {
      setGateRedeeming(false);
    }
  };

  // Auto-load Cookout feed after vouch
  useEffect(() => {
    if (user?.is_vouched && feedType === 'cookout' && cookoutPosts.length === 0) {
      fetchCookout();
    }
  }, [user?.is_vouched, feedType, cookoutPosts.length, fetchCookout]);

  const currentPosts = feedType === 'cookout' ? cookoutPosts : posts;
  const isLoading = loading || cookoutLoading;
  const blockIsQuiet = feedType === 'block' && currentPosts.length < 5;
  const cookoutAccess = user?.is_vouched || user?.is_admin;
  const roomKicker = feedType === 'block' ? 'Public Layer' : 'Trust Layer';
  const roomTitle = feedType === 'block'
    ? 'The neighborhood is open. Bonita keeps the sidewalk warm while the people pull up.'
    : cookoutAccess
      ? 'The Cookout is for vouched people, real talk, and social accountability.'
      : 'The Cookout is invite-only. Somebody has to bring you inside.';
  const roomMeta = feedType === 'block'
    ? [
        blockIsQuiet ? 'Bonita can spark the room when traffic is light' : `${currentPosts.length} posts in the current mix`,
        liveStoop ? 'The Stoop is live' : 'Conversation-first, low-friction energy',
        hbcuStory ? 'HBCU.news is in rotation' : 'Culture stories land here first',
      ]
    : [
        cookoutAccess ? 'You made it inside the trusted room' : 'Need a Plate code to enter',
        voucherProfile ? `Vouched in by @${voucherProfile.username}` : 'Every plate ties back to real trust',
        cookoutAccess ? 'Warmer tone, tighter accountability' : 'Earned access, not casual clicking',
      ];

  const cardForPost = (post) =>
    feedType === 'block' && (post?.user_id === 'bonita' || post?.is_spark) ? (
      <BonitaErrorBoundary>
        <PostCard post={post} />
      </BonitaErrorBoundary>
    ) : (
      <PostCard post={post} />
    );

  return (
    <div className="relative" data-testid="home-page">
      {/* Welcome Modal */}
      <WelcomeModal open={showWelcome} onOpenChange={setShowWelcome} />

      {/* Feed tabs */}
      <div className="feed-top">
        <button
          className={cn("tab", feedType === 'block' && "on")}
          onClick={() => syncFeedTab('block')}
          data-testid="feed-block-tab"
        >
          &#11035; The Block
        </button>
        <button
          className={cn("tab ckt", feedType === 'cookout' && "on")}
          onClick={() => syncFeedTab('cookout')}
          data-testid="feed-cookout-tab"
        >
          {!(user?.is_vouched || user?.is_admin) && <Lock className="h-3 w-3" />}
          &#127869; The Cookout <span className="tab-pip" />
        </button>
      </div>

      <div className={cn("feed-room-banner", feedType === 'cookout' && "cookout")}>
        <div className="feed-room-copy">
          <div className="feed-room-kicker">{roomKicker}</div>
          <div className="feed-room-title">{roomTitle}</div>
        </div>
        <div className="feed-room-pills">
          {roomMeta.map((item) => (
            <span key={item} className="feed-room-pill">{item}</span>
          ))}
        </div>
      </div>
      <NeighborhoodPulseBar
        feedType={feedType}
        stats={pulseStats}
        bonitaPost={pulseBonitaPost}
        calendarData={calendarPulseData}
        liveStoop={liveStoop}
        cookoutAccess={cookoutAccess}
        voucherProfile={voucherProfile}
        navigate={navigate}
      />
      {feedType === 'block' && (
        <DailyRitualCard brief={dailyBrief} navigate={navigate} openCreateSheet={openCreateSheet} isDark={isDark} />
      )}
      {feedType === 'cookout' && cookoutAccess && (
        <TrustLedgerCard voucherProfile={voucherProfile} navigate={navigate} isDark={isDark} />
      )}

      {/* Energy filter bar */}
      {feedType === 'block' && (
        <div className="energy-bar">
          <button
            className={cn("energy-chip", energyFilter === null && "on")}
            onClick={() => setEnergyFilter(null)}
          >
            All
          </button>
          {ENERGIES.map((e) => (
            <button
              key={e.key}
              className={cn("energy-chip", energyFilter === e.key && "on")}
              onClick={() => setEnergyFilter(energyFilter === e.key ? null : e.key)}
            >
              <span>{e.icon}</span>
              {e.label}
            </button>
          ))}
        </div>
      )}

      {/* Feed content */}
      {isLoading && currentPosts.length === 0 ? (
        <div className="p-4 space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-3 py-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      ) : feedType === 'cookout' && !user?.is_vouched && !user?.is_admin ? (
        // Cookout gate
        <div className="flex items-center justify-center cookout-room-shell" style={{ minHeight: '60vh' }}>
          {cookoutWelcome && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-amber-500/10 backdrop-blur-sm animate-in fade-in duration-300">
              <div className="text-center">
                <div className="text-6xl mb-4">&#127869;</div>
                <p className={cn("text-2xl font-display tracking-widest uppercase", textClass)}>
                  Welcome to The Cookout
                </p>
              </div>
            </div>
          )}
          <div className="text-center px-6 w-full max-w-md">
            <div className="mx-auto mb-4 w-fit rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.3em] text-amber-500">
              Invite-only room
            </div>
            <div className="text-5xl mb-5">&#127869;</div>
            <p className={cn(textClass, "text-2xl mb-2 font-display tracking-widest uppercase")}>
              You Got a Plate?
            </p>
            <p className={cn(textMutedClass, "text-sm mb-6 leading-relaxed")}>
              The Cookout is where trust matters more than volume. If someone vouched for you,
              your Plate code is the way in.
            </p>
            {voucherProfile && (
              <p className={cn("mb-4 text-xs", isDark ? "text-white/45" : "text-gray-500")}>
                Your last Plate came through @{voucherProfile.username}. That trust follows you in here.
              </p>
            )}
            <div className={cn("mb-6 grid grid-cols-1 gap-2 text-left text-xs", isDark ? "text-white/55" : "text-gray-600")}>
              <div className="flex items-center gap-2 rounded-none border border-white/10 px-3 py-2">
                <Shield className="h-3.5 w-3.5 text-amber-500" />
                Brought in through trust, not open signups
              </div>
              <div className="flex items-center gap-2 rounded-none border border-white/10 px-3 py-2">
                <Users className="h-3.5 w-3.5 text-amber-500" />
                Warmer room, tighter accountability
              </div>
            </div>
            <form onSubmit={handleRedeemFromGate} className="space-y-3">
              <Input
                value={gateRedeemCode}
                onChange={(e) => setGateRedeemCode(e.target.value.toUpperCase())}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text').trim();
                  setGateRedeemCode(pasted.toUpperCase());
                  clearTimeout(autoSubmitRef.current);
                  autoSubmitRef.current = setTimeout(() => handleRedeemFromGate(null, pasted), 800);
                }}
                placeholder="Paste your Plate code here..."
                className={cn(
                  "text-center bg-transparent rounded-none uppercase tracking-widest text-lg font-mono h-12",
                  isDark
                    ? "border-white/20 focus:border-amber-500 placeholder:text-white/20"
                    : "border-gray-300 focus:border-amber-500 placeholder:text-gray-300"
                )}
                maxLength={9}
                disabled={gateRedeeming}
                autoFocus
              />
              <Button
                type="submit"
                disabled={gateRedeeming || !gateRedeemCode.trim()}
                className="w-full rounded-none font-display tracking-wider text-sm h-11 bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40"
              >
                {gateRedeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enter The Cookout \u2192'}
              </Button>
            </form>
            <p className={cn("text-xs mt-6", isDark ? "text-white/25" : "text-gray-400")}>
              No invite yet?{' '}
              <button onClick={() => navigate('/vouch')} className="text-amber-500 hover:text-amber-400 underline underline-offset-2">
                Learn how The Vouch works \u2192
              </button>
            </p>
          </div>
        </div>
      ) : currentPosts.length === 0 ? (
        <div className={cn("flex items-center justify-center", feedType === 'cookout' && "cookout-room-shell")} style={{ minHeight: '60vh' }}>
          <div className="text-center px-6 max-w-md mx-auto">
            <p className={cn(textMutedClass, "text-base mb-2 font-display tracking-wide uppercase")}>
              {feedType === 'block' ? "The Block is quiet" : "The Cookout is empty"}
            </p>
            <p className={cn(isDark ? "text-white/35" : "text-gray-500", "text-sm mb-5 leading-relaxed")}>
              {feedType === 'block'
                ? "Drop a take, pull up on The Stoop, or let Bonita spark something while the community gathers."
                : "This room opens slower on purpose. Bring something real, and keep it solid once you're inside."}
            </p>
            {feedType === 'block' && (
              <div className="flex flex-col sm:flex-row gap-2 justify-center mb-8">
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    'rounded-none font-display tracking-wider text-xs border-amber-500/50 text-amber-500 hover:bg-amber-500/10',
                    isDark && 'border-amber-500/40'
                  )}
                  onClick={() => navigate('/bonita')}
                >
                  Ask Bonita
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    'rounded-none font-display tracking-wider text-xs border-amber-500/50 text-amber-500 hover:bg-amber-500/10',
                    isDark && 'border-amber-500/40'
                  )}
                  onClick={() => navigate('/calendar')}
                >
                  Culture Calendar
                </Button>
                <Button
                  type="button"
                  className="rounded-none font-display tracking-wider text-xs bg-amber-500 text-black hover:bg-amber-400"
                  onClick={openCreateSheet}
                >
                  Drop something
                </Button>
              </div>
            )}
            {feedType === 'cookout' && (
              <div className="flex flex-col sm:flex-row gap-2 justify-center mb-8">
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    'rounded-none font-display tracking-wider text-xs border-amber-500/50 text-amber-500 hover:bg-amber-500/10',
                    isDark && 'border-amber-500/40'
                  )}
                  onClick={() => navigate('/vouch')}
                >
                  How Vouching Works
                </Button>
                <Button
                  type="button"
                  className="rounded-none font-display tracking-wider text-xs bg-amber-500 text-black hover:bg-amber-400"
                  onClick={openCreateSheet}
                >
                  Drop to The Cookout
                </Button>
              </div>
            )}
            {feedType !== 'cookout' && <PeopleDiscover limit={6} fullWidth />}
          </div>
        </div>
      ) : (
        // Traditional scroll feed with PostCards
        <div ref={scrollContainerRef} className={cn("feed-scroll", feedType === 'cookout' && "feed-scroll-cookout")}>
          {currentPosts.map((post, index) => (
            <div key={post.post_id}>
              {cardForPost(post)}
              {index === 0 && feedType === 'block' && <RisingVoicesStrip />}
              {index === 1 && feedType === 'block' && liveStoop && (
                <LiveStoopCard stoop={liveStoop} navigate={navigate} />
              )}
              {index === 2 && feedType === 'block' && hbcuStory && (
                <HBCUNewsCard story={hbcuStory} navigate={navigate} />
              )}
            </div>
          ))}
          {loadingMore && (
            <div className="flex items-center justify-center py-8">
              <div className={cn("w-6 h-6 border-2 rounded-full animate-spin", isDark ? "border-white/20 border-t-white" : "border-gray-200 border-t-gray-700")} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
